// src/lib/utils.js
// Shared calculation utilities used across Reports, Service Log, and Fleet pages.
// Single source of truth — import from here, never duplicate this logic.

/**
 * Calculate savings for a single tagout_log record.
 *
 * Value hierarchy (agreed 2026-05-13):
 * - Tool with no service history       → MSRP from tool_models (handled at fleet level)
 * - Repaired tool                      → null until actual_repair_cost is entered
 * - Replaced tool                      → null until replacement_msrp is entered
 *
 * Savings logic:
 * - Repaired, has actual_repair_cost   → tool_models MSRP minus actual cost
 * - Repaired, is_warranty              → tool_models MSRP (full savings, $0 cost)
 * - Replaced, has replacement_msrp    → replacement_msrp minus actual cost
 * - Replaced, is_warranty             → replacement_msrp (full savings, $0 cost)
 * - Missing MSRP data                 → null (shown as "—", never $0)
 *
 * @param {object} record - tagout_log row with tools.tool_models.msrp joined
 * @returns {number|null} savings amount, or null if insufficient data
 */
export function calcSavings(record) {
  if (!record) return null;

  const isReplaced = record.repair_or_replace === 'replaced';
  const isTraded = record.repair_or_replace === 'traded';
  const isRepaired = record.repair_or_replace === 'repaired';
  const isWarranty = record.is_warranty === true;
  const actualCost = isWarranty ? 0 : (parseFloat(record.actual_repair_cost) ?? null);

  if (isReplaced || isTraded) {
    const msrp = parseFloat(record.replacement_msrp) || null;
    if (msrp == null) return null;
    return isWarranty ? msrp : msrp - (actualCost || 0);
  }

  if (isRepaired) {
    const msrp = parseFloat(record.tools?.tool_models?.msrp) || null;
    if (msrp == null) return null;
    if (!isWarranty && actualCost == null) return null;
    return isWarranty ? msrp : msrp - actualCost;
  }

  return null;
}

/**
 * Calculate current tool value for fleet/reporting purposes.
 *
 * Value hierarchy:
 * - Never had a tagout                 → MSRP from tool_models
 * - Has been repaired                  → null until actual_repair_cost entered
 * - Has been replaced                  → null until replacement_msrp entered
 *
 * @param {object} tool - tools row with tool_models.msrp joined
 * @param {Array}  tagouts - all tagout_log rows for this tool (filtered by tool_id)
 * @returns {number|null}
 */
export function calcToolValue(tool, tagouts = []) {
  const returned = tagouts.filter(t => t.status === 'Returned');

  if (returned.length === 0) {
    // No service history — use MSRP
    return parseFloat(tool?.tool_models?.msrp) || null;
  }

  // Most recent returned tagout determines current value basis
  const latest = returned.sort((a, b) => new Date(b.return_date) - new Date(a.return_date))[0];

  if (latest.repair_or_replace === 'replaced') {
    return parseFloat(latest.replacement_msrp) || null;
  }

  if (latest.repair_or_replace === 'repaired') {
    return parseFloat(latest.actual_repair_cost) != null
      ? parseFloat(tool?.tool_models?.msrp) || null
      : null;
  }

  return null;
}

/**
 * Resolve the current value of a tool for display and calculations.
 * Single source of truth used by Fleet Inventory, Reports, and Service Log.
 *
 * Hierarchy:
 * 1. cost field on tool record (actual purchase price if known)
 * 2. tool_models.msrp (retail fallback if no cost)
 * 3. null if neither exists
 *
 * Note: repair/replace history overrides are handled at the tagout level
 * via calcSavings() — this function is for current tool value only.
 *
 * @param {object} tool - tools row, optionally with tool_models.msrp joined
 * @returns {number|null}
 */
export function resolveToolValue(tool) {
  if (!tool) return null;
  const cost = parseFloat(tool.cost);
  if (!isNaN(cost) && cost > 0) return cost;
  const msrp = parseFloat(tool.tool_models?.msrp);
  if (!isNaN(msrp) && msrp > 0) return msrp;
  return null;
}

/**
 * Format a number as USD currency string.
 * Returns '—' for null/undefined.
 */
export function fmt$(v) {
  if (v == null || v === '') return '—';
  return '$' + Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a date string as localized short date.
 * Returns '—' for null/undefined.
 */
export function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

/**
 * Acquisition type options — single source of truth for dropdowns and labels.
 */
export const ACQUISITION_TYPES = [
  { value: 'unknown',        label: 'Unknown' },
  { value: 'purchased_new',  label: 'Purchased new (full retail)' },
  { value: 'purchased_sale', label: 'Purchased on sale' },
  { value: 'repair_shop',    label: 'Acquired from repair shop' },
  { value: 'warranty',       label: 'Warranty replacement' },
];

export function acquisitionLabel(type) {
  return ACQUISITION_TYPES.find(t => t.value === type)?.label || 'Unknown';
}

/**
 * Calculate acquisition savings for a single tool record.
 * - purchased_new  → null (paid full retail, no savings)
 * - unknown        → null (intent unknown)
 * - purchased_sale → MSRP minus cost
 * - repair_shop    → MSRP minus cost
 * - warranty       → full MSRP (paid $0)
 */
export function calcAcquisitionSavings(tool) {
  if (!tool) return null;
  const type = tool.acquisition_type || 'unknown';
  if (type === 'unknown' || type === 'purchased_new') return null;
  const msrp = parseFloat(tool.tool_models?.msrp) || null;
  if (msrp == null) return null;
  if (type === 'warranty') return msrp;
  const cost = parseFloat(tool.cost);
  if (isNaN(cost)) return null;
  return msrp - cost;
}